import { SocialCallback } from '@h2hspark/auth-ui';
import * as React from 'react';
import type { Location } from 'react-router-dom';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { ProtectedRoute } from './auth/protected-route';
import { MemberAccountSetUpPage } from './domains/account-claim/set-up-page';
import {
  AttendeeProfileOverlay,
  AttendeeProfilePage,
} from './domains/attendee-preview/attendee-profile-page';
import { AttendeeRosterPage } from './domains/attendee-preview/attendee-roster-page';
import { CaseStudyPage } from './domains/cms/case-study-page';
import { CmsPreviewPage } from './domains/cms/preview-page';
import { SlugRoutePage } from './domains/cms/slug-route-page';
import { ConnectionDetailPage } from './domains/connections/connection-detail-page';
import { ConnectionRequestsPage } from './domains/connections/connection-requests-page';
import { MemberConnectionsPage } from './domains/connections/connections-page';
import { EventConnectionRequestsPage } from './domains/connections/event-connection-requests-page';
import { EventDetailPage } from './domains/event-discovery/event-detail-page';
import { CheckoutPage } from './domains/event-orders/checkout-page';
import { OrderDetailPage } from './domains/event-orders/order-detail-page';
import { OrderReceiptDocumentPage } from './domains/event-orders/order-receipt-document-page';
import { OrderReceiptPage } from './domains/event-orders/order-receipt-page';
import { TicketsAndOrdersPage } from './domains/event-orders/tickets-and-orders-page';
import { TicketPage } from './domains/event-tickets/ticket-page';
import { FollowUpDetailPage } from './domains/follow-up/follow-up-detail-page';
import { EventMatchesPage } from './domains/matching/event-matches-page';
import { MatchDetailOverlay, MatchDetailPage } from './domains/matching/match-detail-page';
import { MemberConnectPage } from './domains/member-in-event/connect-page';
import { ChangePasswordPage } from './domains/members/change-password-page';
import { ConfirmEmailPage } from './domains/members/confirm-email-page';
import { MatchingPreferencesPage } from './domains/members/matching-preferences-page';
import { MyAccountPage } from './domains/members/my-account-page';
import { PrivacyPage } from './domains/members/privacy-page';
import { MemberRegistrationWizardPage } from './domains/members/registration-wizard-page';
import { SocialChannelsPage } from './domains/members/social-channels-page';
import { MemberVerifyEmailRoute } from './domains/members/verify-email-page';
import { MemberMessagesPage } from './domains/messages/messages-page';
import { ThreadDialog } from './domains/messages/thread-dialog';
import { MemberOrganizerProfilePage } from './domains/organizer-profile/member-profile-page';
import { YourEventsPage } from './domains/your-events/your-events-page';
import { HomePage } from './marketing/home-page';
import { MarketingLayout } from './marketing/marketing-layout';
import { YOUR_EVENTS_DESTINATION } from './member/destinations';
import { MemberHomePage } from './member/member-home-page';
import { MemberLayout } from './member/member-layout';
import { MemberLinkAccountPage } from './member/pages/link-account-page';
import { MemberLoginPage } from './member/pages/login-page';
import { MemberMfaChallengePage } from './member/pages/mfa-challenge-page';
import { MemberRecoveryPage } from './member/pages/recovery-page';

/**
 * One bundle, two surfaces.
 *
 * `/` is the public marketing site — no session, indexable, wide layout. `/app` is the
 * member app: phone-width, installable, and behind a session. They share a bundle because
 * they share a hostname and a design system, and because the path a visitor takes from
 * reading about H2HSpark to using it shouldn't cross an origin.
 *
 * ## The one place a route is rendered twice: the attendee detail (`MEM-056` AC-3)
 *
 * `Web App.dc.html` §2.16 opens a person as a card **over** the list they were in, and `MEM-056`'s
 * overlay table says the same: a centred 460px dialog on desktop, a full-screen view on a phone.
 * Both of those are also addresses — `MEM-047` mails links to them and members bookmark them — so
 * they stay routes, and a link from a list carries the list's own location in
 * `state.backgroundLocation`.
 *
 * When that state is present the router is shown the **background** location, so the list renders
 * and stays mounted, and {@link DetailOverlayRoutes} matches the real one and draws the card over
 * it. With no state — a mailed link, a bookmark, a shared URL — nothing here changes and the
 * standalone page renders as it always has. React Router's own modal-route arrangement, and the
 * reason `MEM-056` AC-3's *"back restores the list at its scroll position"* needs no scroll trick:
 * the list never unmounted.
 */
export function App(): React.ReactElement {
  const location = useLocation();
  const backgroundLocation = (location.state as { backgroundLocation?: Location } | null)
    ?.backgroundLocation;

  return (
    <>
      <Routes location={backgroundLocation ?? location}>
      <Route element={<MarketingLayout />}>
        <Route index element={<HomePage />} />
        {/*
          A case study's own page (`CMS-015` AC-2).

          **Above `:slug` and on a two-segment path, deliberately.** `/{slug}` is already a
          contested address — the organizer profile resolves there first and a marketing page
          second — and adding a third lookup would make every unknown URL cost three requests and
          would put case studies in the same namespace as organizer handles. A case study could
          then shadow, or be shadowed by, an organizer nobody has created yet. Two segments
          collide with nothing and need no precedence rule.
        */}
        <Route path="case-studies/:slug" element={<CaseStudyPage />} />
        {/*
          A draft preview (`CMS-012`). **Above `:slug` and on a two-segment path**, for the reason
          the case-study route above gives — `/{slug}` is already contested between the organizer
          profile and a marketing page, and a third lookup would cost every unknown URL another
          request. `preview` is also a plausible organizer handle, so a single-segment route here
          would let a marketing page shadow, or be shadowed by, somebody's profile.

          Inside `MarketingLayout` because the whole point is that it renders in the real site's
          chrome. Outside any guard because the token *is* the credential: the reader is a
          colleague or a client with no H2HSpark account, which is why the API route is in
          `edge-public-routes.ts`.
        */}
        <Route path="preview/:token" element={<CmsPreviewPage />} />
        {/*
          The public organizer profile (`ORG-017` AC-3), at `h2hspark.com/{slug}` — a top-level
          path, because that is the address the specification gives and the one the editor shows
          an organizer behind the fixed `h2hspark.com/` prefix.

          Since 2026-08-23 this is `SlugRoutePage`, which resolves the organizer profile first
          and falls back to a published CMS page. Route ordering cannot express that precedence —
          React Router matches on the shape of a path, not on whether data exists behind it — so
          two routes at `:slug` could not take turns and the order became a lookup order instead.
          The client's rule is that everything else resolves before the CMS, which is why the
          organizer is asked first and a marketing page only answers an address nothing else
          claims.

          Inside `MarketingLayout` because it is public, indexable chrome; `ProtectedRoute` wraps
          only `/app`, so everything here is anonymous by construction.
        */}
        <Route path=":slug" element={<SlugRoutePage />} />
      </Route>

      <Route path="/app/login" element={<MemberLoginPage />} />
      {/*
        Registration (`MEM-001`…`MEM-004`). **Outside `ProtectedRoute`, and it has to be**: step 1
        is what creates the account, so its caller has no session by definition. Steps 2 to 5 do
        have one — the wizard picks it up from `useMemberSession` after step 1 — and the resume
        path (`MEM-001` AC-7) relies on this route being reachable while signed in *and* signed
        out. `auth-provider`'s redirector already excludes `/app/join*` for that reason.
      */}
      <Route path="/app/join" element={<MemberRegistrationWizardPage />} />
      <Route path="/app/recovery" element={<MemberRecoveryPage />} />
      {/*
        `USR-013`. **Outside `MemberLayout` and outside `ProtectedRoute`, and it has to be**: this
        is where somebody an organizer added to an event turns a place held for them into an
        account, and having one is the point of the visit. The mailed `<rowId>.<secret>` token is
        the whole of the credential (ADR 042).

        `/app/account/verify-email` below is public for **half** the same reason, and the halving
        is the point. Its `?token=` branch is this one exactly — a mailed credential whose reader
        may have no session — while its `?code=` branch keeps `ProtectedRoute`, because those two
        endpoints authenticate on the caller's own access token and there is no anonymous form
        there that could exist. Both paths are named exactly in `MEMBER_APP_PUBLIC_PATHS` rather
        than opening `/app/account/`, which would make My Account and the privacy screen public.
      */}
      <Route path="/app/account/set-up" element={<MemberAccountSetUpPage />} />
      <Route path="/app/mfa-challenge" element={<MemberMfaChallengePage />} />
      <Route path="/app/auth/callback" element={<SocialCallbackRoute />} />
      <Route path="/app/auth/link-account" element={<MemberLinkAccountPage />} />
      {/*
        The member app shell. **`ProtectedRoute` has moved off the layout and onto the routes that
        need it**, which is `MEM-019` AC-1: the Discover list and every public event's detail page
        are available with no account, and a gate on the layout made the whole tree private.

        The shell itself renders for everybody. A signed-out visitor gets the same chrome, the
        same tab bar, and the Sign-in required interstitial (`MEM-019` AC-3) in the content area
        when they reach for a gated tab — rather than the app disappearing out from under them.

        `auth/public-paths.ts` holds the same fact for `UnauthenticatedRedirector`; the two lists
        are one file for the reason that file's header gives.
      */}
      <Route path="/app" element={<MemberLayout />}>
        {/*
          Discover is the default landing surface (`MEM-014` AC-1). Public.

          `MemberHomePage` rather than `DiscoverPage` since `MEM-046`: a visitor with no session
          gets the welcome hero above Discover, a member gets Discover alone. The route stays one
          route on purpose — a separate `/app/welcome` would be the route nobody navigates to,
          since people arrive here from an organizer's link, a QR code or the installed app's
          `start_url`, all of which land on `/app`.
        */}
        <Route index element={<MemberHomePage />} />
        {/*
          The event detail page (`MEM-015`, `MEM-016`). Public, and deliberately so for an
          unlisted event too — `MEM-019` AC-6 has an unlisted event's page resolve for anybody
          holding the link. The API decides which events resolve; this route only decides that no
          session is demanded before asking.
        */}
        <Route path="events/:eventId" element={<EventDetailPage />} />
        {/*
          The organizer public profile, **inside the app shell** (`MEM-018`).

          A second route over the same panel and the same query as the marketing one at `/:slug`
          above — see `domains/organizer-profile/constants.ts` for why the split is at the route.
          The short version: the two readers are different people. A signed-out visitor at
          `h2hspark.com/{slug}` must get public, indexable marketing chrome; a member who tapped
          "Hosted by …" on an event must keep the shell and the tab bar. The parity report found
          the second case landing on the first, and called it what it is — the member "loses the
          tab bar and the app shell".

          **Public**, like Discover and every event detail page (`MEM-019` AC-1): a member often
          arrives at an event from a shared link with no session, and the organizer behind it is
          the same kind of open fact. The Follow control is the part that needs a member, and it
          is the control that withholds itself rather than the page.

          This adds a route and touches no navigation. The tab bar is `MEM-042`'s, and the parity
          report's Q1 records that nobody has ruled on it.
        */}
        <Route path="organizers/:slug" element={<MemberOrganizerProfilePage />} />
        {/*
          `MEM-011`, `MEM-012`, `MEM-025` — the attendee preview, the attendee profile, and the
          matches screens. **All gated**, and none of them may be added to
          `MEMBER_APP_PUBLIC_PATHS`: `MEM-019` AC-2 names the roster, member profiles and matches
          among the things that are not available signed out, and matches are relationship data.

          The event-scoped matches screen is nested under the event (`MEM-025` AC-1 reaches it from
          the registered-member card, and Your Events' rows reach it too). The cross-event screen
          that sat at `/app/matches` is Your Events now (`MEM-025` AC-2, reworded 2026-09-11); the
          address redirects there, below.
        */}
        <Route
          path="events/:eventId/attendees"
          element={
            <ProtectedRoute>
              <AttendeeRosterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/attendees/:attendeeRef"
          element={
            <ProtectedRoute>
              <AttendeeProfilePage />
            </ProtectedRoute>
          }
        />
        {/*
          Checkout and the receipt (`MEM-020`). **Gated, and they must stay that way**: opening
          checkout takes a seat off the room for fifteen minutes, and a receipt is the member's own
          record of what they reserved. Neither may join `MEMBER_APP_PUBLIC_PATHS`.

          Routes rather than the prototype's bottom sheet. A sheet that holds a seat and cannot be
          linked to is a sheet a member loses by refreshing — and `MEM-TKT-010` requires a
          signed-out visitor to be taken through account creation and *returned to payment for the
          same event*, which needs an address to return to.

          Nested under the event because checkout and the reservation confirmation are about one
          event and are reached from it. The **orders list** now exists — `MEM-022` AC-3 built it
          at `account/orders`, below — and it is deliberately somewhere else: that is the member's
          history rather than this event's checkout.
        */}
        <Route
          path="events/:eventId/checkout"
          element={
            <ProtectedRoute>
              <CheckoutPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/receipt"
          element={
            <ProtectedRoute>
              <OrderReceiptPage />
            </ProtectedRoute>
          }
        />
        {/*
          The digital ticket (`MEM-022` AC-1, AC-2). **Gated, and it must stay that way**: the QR
          carries the ticket token, and a token reachable without a session would be a door
          credential served to anyone holding the event's URL. It is nested under the event rather
          than under the orders list because a ticket is about one event and is reached from it —
          and because a free registration has a ticket and no order at all, so an orders list is
          the wrong parent for it. `MEM-022` AC-4's order detail links here, the other way round.
        */}
        <Route
          path="events/:eventId/ticket"
          element={
            <ProtectedRoute>
              <TicketPage />
            </ProtectedRoute>
          }
        />
        {/*
          The member's in-event surface (`MEM-028`…`MEM-031`). **Gated, and it must stay that
          way**: the payload carries the connect code, and a code reachable without a session
          would be one anybody holding the event's URL could have scanned into a connection.

          Nested under the event rather than sitting on the tab bar. The prototype puts Connect in
          the bar, and `MEM-042` owns the member app's navigation — adding a fifth tab here would
          be navigation redesign arriving inside a feature slice. It is reached from the confirmed
          panel, beside Ticket and Matches.
        */}
        <Route
          path="events/:eventId/connect"
          element={
            <ProtectedRoute>
              <MemberConnectPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/matches"
          element={
            <ProtectedRoute>
              <EventMatchesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/matches/:attendeeRef"
          element={
            <ProtectedRoute>
              <MatchDetailPage />
            </ProtectedRoute>
          }
        />
        {/*
          **This event's connection requests** (`MEM-053` AC-6; `Web App.dc.html` §2.5) — what the
          header card's Requests control opens, because the count on that control is this event's
          and the destination has to be too. `/app/connections/requests` is unchanged and is still
          what the connections list opens (`MEM-032` AC-6); this is the same queue, filtered.
        */}
        <Route
          path="events/:eventId/requests"
          element={
            <ProtectedRoute>
              <EventConnectionRequestsPage />
            </ProtectedRoute>
          }
        />
        {/*
          Your Events (`MEM-052`) — the page the navigation's second item opens, and what the
          cross-event matches screen and the follow-up index became (Sumit, 2026-09-11). **Gated**:
          every row is the member's own registrations, match sets and follow-ups, which `MEM-019`
          AC-2 keeps from a signed-out visitor.

          The two old index addresses **redirect here** rather than 404 (`MEM-052` AC-6): a member
          with either bookmarked, or holding an old link, lands on the page that now holds what
          they were looking for. The per-event screens those indexes led to — and which mailed
          links pin, `/app/events/{id}/matches` and `/app/follow-up/events/{id}` — are unchanged.
        */}
        <Route
          path="your-events"
          element={
            <ProtectedRoute>
              <YourEventsPage />
            </ProtectedRoute>
          }
        />
        <Route path="matches" element={<Navigate to={YOUR_EVENTS_DESTINATION} replace />} />
        {/*
          **Messages** (`MEM-055` AC-1, `MEM-054` AC-2; ADR 052) — the navigation's third item,
          added in the same change as the page behind it so it never opens onto nothing.

          The thread is a **child route**, not a piece of the page's state, and that is `MEM-055`
          AC-6: the new-message notification opens *that thread*, directly for a signed-in member
          and after sign-in otherwise, so it needs an address. `MemberMessagesPage` renders it
          through its `<Outlet/>` — a dialog over the list on desktop, full screen on a phone
          (`MEM-054` AC-2) — which also keeps the list mounted behind it, and with it `MEM-042`
          AC-6's *"back returns to the list at its previous scroll position"*.

          **Gated**, and it must stay gated: a thread is the most private thing this platform
          holds about two people (ADR 052), and it may never join `MEMBER_APP_PUBLIC_PATHS`. A
          signed-out visitor following a notification link gets the sign-in interstitial and
          arrives here afterwards, which is AC-6's second half.
        */}
        <Route
          path="messages"
          element={
            <ProtectedRoute>
              <MemberMessagesPage />
            </ProtectedRoute>
          }
        >
          <Route path=":connectionRef" element={<ThreadDialog />} />
        </Route>
        <Route
          path="connections"
          element={
            <ProtectedRoute>
              <MemberConnectionsPage />
            </ProtectedRoute>
          }
        />
        {/*
          `MEM-036` — one event's follow-up. **Gated**, and it must stay gated: a follow-up list is
          the member's own relationship data, which `MEM-019` AC-2 puts among the things that are
          not available signed out. It may not join `MEMBER_APP_PUBLIC_PATHS`.

          The index that used to sit at `follow-up` is Your Events' *My follow up* column now
          (`MEM-036` AC-1, `MEM-052` AC-5), and its address opens that page (`MEM-052` AC-6).
        */}
        <Route path="follow-up" element={<Navigate to={YOUR_EVENTS_DESTINATION} replace />} />
        {/* `MEM-032` AC-6. Above `connections/:connectionRef` so the literal wins the match — a
            request reference is not a connection reference, and the two are the same shape. */}
        <Route
          path="connections/requests"
          element={
            <ProtectedRoute>
              <ConnectionRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="follow-up/events/:eventId"
          element={
            <ProtectedRoute>
              <FollowUpDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="connections/:connectionRef"
          element={
            <ProtectedRoute>
              <ConnectionDetailPage />
            </ProtectedRoute>
          }
        />
        {/*
          The member identity screens (`MEM-005`…`MEM-010`), all of them under `account/`.

          **`/app/profile` and `/app/profile/events` are gone, and nothing redirects.** Sumit,
          reviewing Phase 5 on 2026-09-12: *"profile is still taking to old profile page"*, and,
          asked whether the old address should redirect, *"we don't need to redirect the old
          profile url, it can just be removed."* That overturns ruling 38, which had kept the
          screen. The desktop header's avatar and the phone tab bar's **You** now open
          `/app/account` — the one screen `Web App.dc.html` §2.15 draws — so the two ways in that
          used to disagree no longer do.

          Nothing routes `profile` any more, so the address falls through to this file's own `*`
          catch-all and lands on the marketing home — **the same thing `/app/nonsense` does**, which
          is exactly the point. A removed address should be indistinguishable from one that never
          existed; a redirect to `/app/account` would have made it look like a screen that moved,
          and Sumit ruled it out.
        */}
        <Route
          path="account"
          element={
            <ProtectedRoute>
              <MyAccountPage />
            </ProtectedRoute>
          }
        />
        {/*
          Change password, on its own page (Phase 5).

          It was an inline card at `/app/account#password` — the anchor the settings row pointed
          at, and the one `member-app-parity.md` §6 named. The merged Your account screen is a
          two-column layout with a profile form in it, and a second Save in that column would
          undo `MEM-007` AC-4's single pinned footer. `change-password-page.tsx` carries the
          argument; the fragment still resolves, because a fragment is not a route and
          `/app/account` still renders.
        */}
        <Route
          path="account/password"
          element={
            <ProtectedRoute>
              <ChangePasswordPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="account/social-channels"
          element={
            <ProtectedRoute>
              <SocialChannelsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="account/matching-preferences"
          element={
            <ProtectedRoute>
              <MatchingPreferencesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="account/privacy"
          element={
            <ProtectedRoute>
              <PrivacyPage />
            </ProtectedRoute>
          }
        />
        {/*
          Tickets & orders, one order, and its receipt (`MEM-022` AC-3, AC-4, AC-5).

          **Gated, and they must stay that way.** An order is the member's own financial record —
          what they were charged, on which card, under which terms — and `MEM-022` AC-7 makes it
          readable only by the member who owns it. The server enforces that itself
          (`EventOrdersService.readMyOrder` answers 404 for somebody else's id, indistinguishable
          from one that never existed), so `ProtectedRoute` here is the first of two gates rather
          than the only one: it keeps a signed-out visitor from reaching a screen that would only
          fail, and it is not what makes the boundary true.

          **Under `account/` rather than under the event**, unlike checkout, the receipt and the
          ticket. Those three are about one event and are reached from it. These are the member's
          history: AC-3 says Tickets & orders *"is reached from the account page"*, and the path
          says the same thing. `MEM_PROFILE_ROUTES.ticketsAndOrders` and the orders slice's own
          `TICKETS_AND_ORDERS_PATH` both name this string, and a test asserts they agree.

          The receipt is a **route, not the prototype's modal**. `Web App.dc.html` opens it over
          the account page with Print and Close. A receipt is the one artefact in this product
          somebody bookmarks, mails to themselves, or opens on a laptop to print six weeks later,
          and a modal has no address to do any of that with.
        */}
        <Route
          path="account/orders"
          element={
            <ProtectedRoute>
              <TicketsAndOrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="account/orders/:orderId"
          element={
            <ProtectedRoute>
              <OrderDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="account/orders/:orderId/receipt"
          element={
            <ProtectedRoute>
              <OrderReceiptDocumentPage />
            </ProtectedRoute>
          }
        />
        {/* Reached from a mailed link, and **still behind `ProtectedRoute`**: the link proves the
            member controls the new address, and the session proves they are the account holder.
            A link alone would let whoever intercepted the mail finish a change they did not start. */}
        <Route
          path="account/confirm-email"
          element={
            <ProtectedRoute>
              <ConfirmEmailPage />
            </ProtectedRoute>
          }
        />
        {/*
          Verify your email (`USR-003` AC-5), and since `ADM-039` AC-3 the far end of an
          administrator's *Send a verification email* as well.

          **The gate is inside `MemberVerifyEmailRoute`, not here, and that is the one thing to
          know before reading this line as an ungated route.** Two different credentials arrive at
          this one address and only one of them has a session behind it:

            - `?code=` — `USR-003` AC-5's six digits. Both endpoints under it authenticate on the
              caller's own access token: the send resolves the mailbox from the session, the
              complete records the proof against the session's user id. There is no anonymous form
              there that could exist, so that branch is wrapped in `ProtectedRoute` exactly as this
              route used to be, and a signed-out member still meets the interstitial carrying
              `next`.
            - `?token=` — an `address-proof` credential link (ADR 042), spent by the public
              `POST /organizers/onboarding/email-confirmations`. **The token is the whole of the
              credential and the endpoint takes no session.** Gating it would have asked somebody
              to prove the account before being allowed to prove the mailbox, which is the loop the
              mail was sent to get them out of.

          React Router cannot branch on a query parameter, so the branch is a component; it lives
          beside the two panels in `verify-email-page.tsx`, which argues all of this at length.
          `MEMBER_APP_PUBLIC_PATHS` names this path exactly — see `auth/public-paths.ts`, which now
          carries the same reasoning for `set-up` and `verify-email` rather than opposite ones.
        */}
        <Route path="account/verify-email" element={<MemberVerifyEmailRoute />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {backgroundLocation === undefined ? null : <DetailOverlayRoutes />}
    </>
  );
}

/**
 * The detail routes again, as overlays over whatever list the member opened them from.
 *
 * **Every path here must also exist in the tree above**, or a mailed link to it would fall through
 * to the catch-all. They are the same components either way — the overlay export and the page
 * export of one file, sharing one body — so the two renderings cannot show different things.
 *
 * `ProtectedRoute` is repeated rather than inherited: this is a second `<Routes>`, outside
 * `MemberLayout`, and a gate that applied only to the page behind the overlay would be no gate at
 * all. `MemberLayout`'s own chrome is not repeated, because the background is rendering it.
 */
function DetailOverlayRoutes(): React.ReactElement {
  return (
    <Routes>
      <Route
        path="/app/events/:eventId/attendees/:attendeeRef"
        element={
          <ProtectedRoute>
            <AttendeeProfileOverlay />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/events/:eventId/matches/:attendeeRef"
        element={
          <ProtectedRoute>
            <MatchDetailOverlay />
          </ProtectedRoute>
        }
      />
      {/* Anything else with a background location is not an overlay — the background is already
          on screen and this renders nothing over it. */}
      <Route path="*" element={null} />
    </Routes>
  );
}

/**
 * Bridges `<SocialCallback>` to the router. The callback runs the token exchange and hands
 * back the original destination, which is pushed with `replace` so no spent exchange code
 * is left behind in history.
 */
function SocialCallbackRoute(): React.ReactElement {
  const navigate = useNavigate();
  return (
    <SocialCallback
      onSuccess={(next) => navigate(next, { replace: true })}
      onLinkRequired={(flowId) =>
        navigate(`/app/auth/link-account?flow=${encodeURIComponent(flowId)}`, { replace: true })
      }
      onFailure={(error) => {
        navigate(`/app/login?error=${encodeURIComponent(error.message)}`, { replace: true });
      }}
    />
  );
}
